import { uniqueId } from 'lodash';
import React, { ReactElement, useCallback, useState } from 'react';
import { ExtendedMessageDefinition, NotificationContextType } from './types';

/**
 * React context for managing notifications
 */
export const NotificationContext = React.createContext<NotificationContextType>({
  items: [],
  addNotification: () => {
    throw new Error('No Provider');
  },
  removeNotification: (id: string) => {
    throw new Error('No Provider');
  },
  clearByGroup: (group: string) => {
    throw new Error('No Provider');
  },
});

/**
 * Context provider for Notifications
 * @param {ReactElement} children components that uses notification
 */
export default function NotificationProvider({
  children,
}: {
  children: ReactElement;
}): ReactElement {
  const [items, setItems] = useState<ExtendedMessageDefinition[]>([]);

  const removeNotification = useCallback((id: string) => {
    setItems((preItems) => preItems.filter((value) => value.id !== id));
  }, []);

  const addNotification = useCallback(
    (newItem: ExtendedMessageDefinition) => {
      setItems((preItems) => {
        const id = `notification-${uniqueId()}`;
        const itemToAdd: ExtendedMessageDefinition = {
          ...newItem,
          id,
          dismiss: ((itemId) =>
            function dismissFunction() {
              if (newItem && newItem.dismiss) {
                newItem.dismiss();
              }
              removeNotification(itemId);
            })(id),
        };
        return [...preItems, itemToAdd];
      });
    },
    [removeNotification]
  );

  const clearByGroup = (group: string) => {
    setItems((prevItems) => prevItems.filter((value) => value.group !== group));
  };

  const contextValue = {
    items,
    addNotification,
    removeNotification,
    clearByGroup,
  };
  return (
    <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>
  );
}
